import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { MapPin, ArrowRight } from 'lucide-react';

/* ------------------------------------------------------------------ */
/* Config — territory status can be updated here. State codes use the   */
/* US Census FIPS-mapped 2-letter codes so hover shows a proper label.  */
/* ------------------------------------------------------------------ */
const TERRITORY_STATUS = {
  active: [
    'AL','AK','AZ','AR','CA','CO','DE','FL','GA','IA','ID','KS','LA','MA','MS','MO',
    'MT','NH','NJ','NM','NV','OH','OK','OR','PA','TN','TX','UT','VT','WA','WV','WY',
  ],
  comingSoon:  [],
};

/* Main-map projection config — hoisted to a module-level constant so
   the object reference is stable across renders and doesn't trigger
   unnecessary work inside react-simple-maps. */
const MAIN_PROJECTION_CONFIG = { scale: 1180 };

/* Approximate lon/lat centroids for the 48 contiguous states + DC — used
   to position a pin marker inside each state. Alaska is intentionally
   omitted because the map only renders the contiguous 48. */
const STATE_CENTROIDS = {
  AL: [-86.79, 32.79], AZ: [-111.66, 34.29], AR: [-92.44, 34.90],
  CA: [-119.68, 36.78], CO: [-105.55, 38.99], CT: [-72.76, 41.62],
  DC: [-77.03, 38.90], DE: [-75.51, 38.99], FL: [-81.69, 27.77],
  GA: [-83.64, 32.99], ID: [-114.75, 44.24], IL: [-89.14, 40.35],
  IN: [-86.28, 39.85], IA: [-93.21, 42.01], KS: [-96.73, 38.49],
  KY: [-84.87, 37.66], LA: [-91.87, 31.17], ME: [-69.38, 44.69],
  MD: [-76.80, 39.06], MA: [-71.53, 42.23], MI: [-84.54, 43.32],
  MN: [-93.90, 45.69], MS: [-89.68, 32.74], MO: [-92.29, 38.46],
  MT: [-110.45, 46.92], NE: [-98.27, 41.13], NV: [-116.63, 38.31],
  NH: [-71.56, 43.45], NJ: [-74.52, 40.30], NM: [-106.24, 34.84],
  NY: [-74.95, 42.17], NC: [-79.81, 35.63], ND: [-99.78, 47.53],
  OH: [-82.76, 40.39], OK: [-96.93, 35.57], OR: [-122.07, 44.57],
  PA: [-77.21, 40.59], RI: [-71.51, 41.68], SC: [-80.94, 33.86],
  SD: [-99.44, 44.30], TN: [-86.69, 35.75], TX: [-97.56, 31.06],
  UT: [-111.86, 40.15], VT: [-72.71, 44.05], VA: [-78.17, 37.77],
  WA: [-121.49, 47.40], WV: [-80.95, 38.49], WI: [-89.62, 44.27],
  WY: [-107.30, 42.75],
};

/* us-atlas states-10m TopoJSON — hosted on jsDelivr, cached by CDN. */
const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

/* FIPS → 2-letter mapping needed for hover / status lookups. */
const FIPS_TO_CODE = {
  '01': 'AL','02':'AK','04':'AZ','05':'AR','06':'CA','08':'CO','09':'CT','10':'DE',
  '11':'DC','12':'FL','13':'GA','15':'HI','16':'ID','17':'IL','18':'IN','19':'IA',
  '20':'KS','21':'KY','22':'LA','23':'ME','24':'MD','25':'MA','26':'MI','27':'MN',
  '28':'MS','29':'MO','30':'MT','31':'NE','32':'NV','33':'NH','34':'NJ','35':'NM',
  '36':'NY','37':'NC','38':'ND','39':'OH','40':'OK','41':'OR','42':'PA','44':'RI',
  '45':'SC','46':'SD','47':'TN','48':'TX','49':'UT','50':'VT','51':'VA','53':'WA',
  '54':'WV','55':'WI','56':'WY',
};

const statusFor = (code) => {
  if (TERRITORY_STATUS.active.includes(code)) return 'active';
  if (TERRITORY_STATUS.comingSoon.includes(code)) return 'coming-soon';
  return 'none';
};

/* Shared reusable defs (raster relief patterns + perimeter glow). */
const MAP_DEFS = (
  <defs>
    {/* Raster "mountain relief" — a procedurally generated grayscale heightmap
        (see /public/textures/mountain-relief.png). Tiled at userSpaceOnUse so
        the terrain scale looks natural regardless of state size. */}
    <pattern id="ionic-stone" x="0" y="0" width="140" height="140" patternUnits="userSpaceOnUse">
      <image href="/textures/mountain-relief.png" x="0" y="0" width="140" height="140" preserveAspectRatio="xMidYMid slice" />
    </pattern>
    <pattern id="ionic-stone-active" x="0" y="0" width="140" height="140" patternUnits="userSpaceOnUse">
      <image href="/textures/mountain-relief-active.png" x="0" y="0" width="140" height="140" preserveAspectRatio="xMidYMid slice" />
    </pattern>
    <pattern id="ionic-stone-coming" x="0" y="0" width="140" height="140" patternUnits="userSpaceOnUse">
      <image href="/textures/mountain-relief-coming.png" x="0" y="0" width="140" height="140" preserveAspectRatio="xMidYMid slice" />
    </pattern>

    {/* Perimeter neon-green glow — applied to the outer state paths group */}
    <filter id="ionic-glow" x="-10%" y="-10%" width="120%" height="120%">
      <feGaussianBlur in="SourceAlpha" stdDeviation="6" result="blur" />
      <feFlood floodColor="#19DF02" floodOpacity="0.95" />
      <feComposite in2="blur" operator="in" result="glow" />
      <feMerge>
        <feMergeNode in="glow" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  </defs>
);

/* Small pin used in the legend + info panel. */
const PinIcon = ({ color, className }) => (
  <div
    className={className}
    style={{ color, filter: `drop-shadow(0 0 8px ${color}bb)` }}
  >
    <MapPin className="w-6 h-6" strokeWidth={2} fill={color} stroke="#000" />
  </div>
);

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */
const TerritoryMap = () => {
  const compositionRef = useRef(null);
  const [hovered, setHovered] = useState(null);
  const [clickedCode, setClickedCode] = useState(null);
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0 });

  const hoveredMeta = useMemo(() => {
    if (!hovered) return null;
    return { code: hovered.code, name: hovered.name, status: statusFor(hovered.code) };
  }, [hovered]);

  /* Convert a mouse event's page coordinates into coordinates relative
     to the map composition box so the tooltip stays anchored inside the
     scrollable map container. Falls back gracefully if the ref hasn't
     mounted yet. */
  const toLocalCoords = (e) => {
    const box = compositionRef.current?.getBoundingClientRect();
    if (!box) return { x: 0, y: 0 };
    return { x: e.clientX - box.left, y: e.clientY - box.top };
  };

  const onMove = (e) => {
    if (!tooltip.show) return;
    const p = toLocalCoords(e);
    setTooltip((t) => ({ ...t, x: p.x, y: p.y }));
  };

  const fillFor = (code, isHover) => {
    const s = statusFor(code);
    const isClicked = clickedCode === code;
    if (s === 'active') {
      // Active states illuminate neon green ONLY when hovered or clicked.
      if (isHover || isClicked) return '#19FD02';
      return 'url(#ionic-stone)';
    }
    if (s === 'coming-soon') return 'url(#ionic-stone-coming)';
    // Unlisted (available) states: never illuminate green on hover/click.
    return 'url(#ionic-stone)';
  };

  const renderGeography = (geo, opts = {}) => {
    const fips = String(geo.id).padStart(2, '0');
    const code = FIPS_TO_CODE[fips];
    if (!code) return null; // skip DC / territories
    if (opts.only && !opts.only.includes(code)) return null;
    if (opts.exclude && opts.exclude.includes(code)) return null;

    const isHover = hovered && hovered.code === code;
    const isClicked = clickedCode === code;
    const status = statusFor(code);
    const sw = opts.strokeScale || 1;
    const solidFill = opts.solidFill;

    const fillNow = solidFill
      ? (isHover ? '#151b12' : solidFill)
      : fillFor(code, isHover);

    const isIlluminated = status === 'active' && (isHover || isClicked);
    const dropShadow = isIlluminated
      ? 'drop-shadow(0 0 8px rgba(25,253,2,0.85))'
      : 'none';

    return (
      <Geography
        key={geo.rsmKey}
        geography={geo}
        data-testid={`state-${code}`}
        onMouseEnter={(e) => { const p = toLocalCoords(e); setHovered({ code, name: geo.properties.name }); setTooltip({ show: true, x: p.x, y: p.y }); }}
        onMouseMove={onMove}
        onMouseLeave={() => { setHovered(null); setTooltip((t) => ({ ...t, show: false })); }}
        onClick={() => { if (status === 'active') setClickedCode((c) => (c === code ? null : code)); }}
        fill={fillNow}
        stroke="#ffffff"
        strokeWidth={(isHover ? 0.9 : 0.6) * sw}
        strokeLinejoin="round"
        style={{
          outline: 'none',
          cursor: 'pointer',
          transition: 'fill 300ms ease, filter 300ms ease, stroke-width 200ms ease',
          filter: dropShadow,
        }}
      />
    );
  };

  return (
    <section
      id="territory"
      data-testid="territory-map"
      className="relative bg-black border-t border-[color:var(--green)]/15 overflow-hidden"
    >
      {/* Atmospheric background — soft radial vignettes + smoke bands */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_20%_10%,rgba(25,223,2,0.10),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(1000px_500px_at_80%_90%,rgba(255,255,255,0.05),transparent_65%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(600px_260px_at_10%_80%,rgba(200,200,200,0.06),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(500px_240px_at_90%_20%,rgba(200,200,200,0.05),transparent_70%)]" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-4 md:px-6 py-14 md:py-20">
        {/* ============ HEADER ============ */}
        <div className="max-w-[820px]">
          <div className="reveal font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--green)] mb-3">
            Build With Ionic
          </div>
          <h2 className="reveal font-display text-[32px] sm:text-[42px] md:text-[54px] leading-[0.98] tracking-[-0.01em] text-white uppercase">
            Your Market. <span className="text-[color:var(--green)] drop-shadow-[0_0_18px_rgba(25,223,2,0.4)]">Your Territory.</span>
          </h2>
          <p className="reveal mt-5 max-w-[620px] text-white/75 text-[14.5px] md:text-[16px] leading-[1.6]">
            We&rsquo;re building a network of exceptional operators across the country. Explore available territories and discover where Ionic could be next.
          </p>
        </div>

        {/* ============ MAP COMPOSITION ============ */}
        <div
          ref={compositionRef}
          data-testid="territory-composition"
          className="mt-10 md:mt-12 relative rounded-xl border border-white/10 bg-black overflow-hidden"
          style={{ aspectRatio: '16 / 9' }}
        >
          {/* Atmospheric mountain mist — soft white wisps drifting behind the map.
              Opaque state fills naturally hide the wisps inside the US silhouette
              so the mist only reads in the black negative space around the shape. */}
          <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
            <span className="ionic-mist ionic-mist-1" />
            <span className="ionic-mist ionic-mist-2" />
            <span className="ionic-mist ionic-mist-3" />
            <span className="ionic-mist ionic-mist-4" />
            <span className="ionic-mist ionic-mist-5" />
            <span className="ionic-mist ionic-mist-6" />
          </div>

          {/* Faint ambient gradients (background depth, subtler than the mist) */}
          <div className="pointer-events-none absolute inset-0 z-0">
            <div className="absolute -top-24 left-1/3 w-[520px] h-[220px] bg-white/[0.03] blur-[100px] rounded-full" />
            <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[900px] h-[260px] bg-white/[0.04] blur-[120px] rounded-full" />
          </div>

          {/* Main contiguous US map */}
          <div className="absolute inset-0 z-10" style={{ width: '80%' }}>
            <ComposableMap
              projection="geoAlbersUsa"
              projectionConfig={MAIN_PROJECTION_CONFIG}
              width={980}
              height={620}
              style={{ width: '100%', height: '100%' }}
            >
              {MAP_DEFS}
              <g filter="url(#ionic-glow)">
                <Geographies geography={GEO_URL}>
                  {({ geographies }) => geographies.map((geo) => renderGeography(geo, { exclude: ['AK', 'HI'] }))}
                </Geographies>
              </g>
              {/* Pin markers — green for active franchise territories,
                  yellow for available (unlisted) states. Positioned at
                  each state's centroid and non-interactive so state hover
                  interactions on the paths beneath keep working. */}
              <g style={{ pointerEvents: 'none' }}>
                {Object.entries(STATE_CENTROIDS).map(([code, coords]) => {
                  const isActive = statusFor(code) === 'active';
                  const color = isActive ? '#19FD02' : '#F0B429';
                  return (
                    <Marker key={`pin-${code}`} coordinates={coords}>
                      <g transform="translate(0,-8)">
                        <path
                          d="M0,-8 C-4.6,-8 -6,-3.2 0,4 C6,-3.2 4.6,-8 0,-8 Z"
                          fill={color}
                          stroke="#000"
                          strokeWidth={0.6}
                          style={{ filter: `drop-shadow(0 0 3px ${color})` }}
                        />
                        <circle cx="0" cy="-4.4" r="1.5" fill="#000" />
                      </g>
                    </Marker>
                  );
                })}
              </g>
            </ComposableMap>
          </div>

          {/* Alaska and Hawaii intentionally omitted — map shows the
              contiguous 48 states only. */}

          {/* ============ RIGHT-SIDE LEGEND ============ */}
          <div
            data-testid="territory-legend"
            className="hidden md:flex absolute right-4 md:right-6 top-1/2 -translate-y-1/2 z-10 flex-col gap-0 rounded-lg border border-[color:var(--green)] bg-black/85 backdrop-blur-sm p-4 md:p-5"
            style={{ boxShadow: '0 0 30px -4px rgba(25,223,2,0.55)', width: 'clamp(200px, 18vw, 240px)' }}
          >
            <div className="flex items-center gap-3 py-1.5">
              <PinIcon color="#19DF02" />
              <div className="font-display uppercase leading-[1.05]">
                <div className="text-white text-[18px] md:text-[20px] tracking-[0.02em]">Active</div>
                <div className="text-white/90 text-[14px] md:text-[15px] tracking-[0.06em]">Territory</div>
              </div>
            </div>
            <div className="my-3 h-px bg-white/80" />
            <div className="flex items-center gap-3 py-1.5">
              <PinIcon color="#F0B429" />
              <div className="font-display uppercase leading-[1.05]">
                <div className="text-white text-[18px] md:text-[20px] tracking-[0.02em]">Future</div>
                <div className="text-white/90 text-[14px] md:text-[15px] tracking-[0.06em]">Territory</div>
              </div>
            </div>
          </div>

          {/* Tooltip — positioned ABSOLUTELY inside the map composition
              so it scrolls with the map on every device and orientation.
              Coordinates are stored relative to the composition box (see
              toLocalCoords). */}
          {tooltip.show && hoveredMeta && (
            <div
              data-testid="state-tooltip"
              className="pointer-events-none absolute z-50 -translate-x-1/2 -translate-y-full mt-[-14px] px-3 py-2 rounded-md bg-black/95 border border-[color:var(--green)]/60 shadow-[0_10px_30px_-10px_rgba(25,223,2,0.6)]"
              style={{ left: tooltip.x, top: tooltip.y }}
            >
              <div className="font-display uppercase tracking-[0.14em] text-[11px] text-white">
                {hoveredMeta.name}
              </div>
              <div className="mt-0.5 font-mono uppercase tracking-[0.2em] text-[9.5px] text-[color:var(--green)]">
                {hoveredMeta.status === 'active' ? 'Active Territory' : 'Future Territory'}
              </div>
            </div>
          )}
        </div>

        {/* ============ MOBILE LEGEND ============ */}
        <div
          data-testid="territory-legend-mobile"
          className="md:hidden mt-4 flex items-stretch gap-3"
        >
          <div className="flex-1 rounded-lg border border-[color:var(--green)] bg-black/85 p-3 flex items-center gap-3" style={{ boxShadow: '0 0 22px -6px rgba(25,223,2,0.55)' }}>
            <PinIcon color="#19DF02" />
            <div className="font-display uppercase leading-[1.05]">
              <div className="text-white text-[15px]">Active</div>
              <div className="text-white/90 text-[12.5px] tracking-[0.06em]">Territory</div>
            </div>
          </div>
          <div className="flex-1 rounded-lg border border-[color:var(--green)] bg-black/85 p-3 flex items-center gap-3" style={{ boxShadow: '0 0 22px -6px rgba(25,223,2,0.55)' }}>
            <PinIcon color="#F0B429" />
            <div className="font-display uppercase leading-[1.05]">
              <div className="text-white text-[15px]">Future</div>
              <div className="text-white/90 text-[12.5px] tracking-[0.06em]">Territory</div>
            </div>
          </div>
        </div>

        {/* Oregon origin caption */}
        <div className="mt-6 flex items-center gap-2 font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-white/55">
          <MapPin className="w-3.5 h-3.5 text-[color:var(--green)]" />
          Oregon · Est. 1994 &mdash; The origin of the Ionic model.
        </div>

        {/* ============ CONVERSION AREA ============ */}
        <div
          data-testid="territory-conversion"
          className="mt-10 md:mt-14 rounded-xl border border-[color:var(--green)]/25 bg-gradient-to-br from-white/[0.03] to-transparent px-5 md:px-8 py-6 md:py-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
        >
          <div className="flex-1">
            <h3 className="font-display text-[22px] md:text-[26px] leading-[1.05] text-white uppercase">
              Ready To Build Your Market?
            </h3>
            <p className="mt-1.5 text-white/70 text-[14px] md:text-[15px]">
              Tell us where you&rsquo;re interested in building Ionic.
            </p>
          </div>
          <Link
            to="/franchise-apply"
            data-testid="territory-conversion-cta"
            className="group inline-flex items-center gap-1.5 bg-[color:var(--green)] text-black hover:bg-white font-display uppercase tracking-[0.10em] text-[13px] md:text-[14px] px-6 md:px-7 py-3 md:py-3.5 rounded-md shadow-[0_0_28px_-4px_rgba(25,223,2,0.9)] transition-colors"
          >
            Explore Franchise Opportunity
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.5} />
          </Link>
        </div>
      </div>

      {/* Component-scoped keyframes for atmospheric mist wisps.
          The mist wisps drift very slowly in different directions/speeds.
          They live in .z-0 behind the state paths, so the opaque US fills
          naturally hide any part of the mist that overlaps the map — the
          mist only appears in the surrounding negative space. */}
      <style>{`
        .ionic-mist {
          position: absolute;
          border-radius: 9999px;
          filter: blur(55px);
          background: radial-gradient(circle at 50% 50%, rgba(255,255,255,0.72), rgba(220,225,230,0.32) 45%, rgba(255,255,255,0) 78%);
          will-change: transform, opacity;
          mix-blend-mode: screen;
        }
        .ionic-mist-1 {
          top: -12%; left: -10%;
          width: 46%; height: 65%;
          opacity: 0.85;
          animation: mist-drift-a 26s ease-in-out infinite alternate;
        }
        .ionic-mist-2 {
          top: 8%; right: -20%;
          width: 44%; height: 62%;
          opacity: 0.55;
          animation: mist-drift-b 30s ease-in-out infinite alternate;
        }
        .ionic-mist-3 {
          bottom: -22%; left: 4%;
          width: 62%; height: 70%;
          opacity: 0.9;
          animation: mist-drift-c 24s ease-in-out infinite alternate;
        }
        .ionic-mist-4 {
          bottom: -18%; right: -6%;
          width: 46%; height: 60%;
          opacity: 0.55;
          animation: mist-drift-d 28s ease-in-out infinite alternate;
        }
        .ionic-mist-5 {
          top: 36%; left: -18%;
          width: 38%; height: 50%;
          opacity: 0.7;
          animation: mist-drift-e 22s ease-in-out infinite alternate;
        }
        .ionic-mist-6 {
          top: -18%; right: 18%;
          width: 42%; height: 52%;
          opacity: 0.72;
          animation: mist-drift-f 32s ease-in-out infinite alternate;
        }
        @keyframes mist-drift-a { 0% { transform: translate3d(0,0,0) scale(1);    opacity: 0.85; }
                                  100% { transform: translate3d(3%, 2%, 0) scale(1.10); opacity: 0.65; } }
        @keyframes mist-drift-b { 0% { transform: translate3d(0,0,0) scale(1);    opacity: 0.80; }
                                  100% { transform: translate3d(-3%,-2%,0) scale(1.12); opacity: 0.60; } }
        @keyframes mist-drift-c { 0% { transform: translate3d(0,0,0) scale(1);    opacity: 0.90; }
                                  100% { transform: translate3d(4%, -3%, 0) scale(1.08); opacity: 0.70; } }
        @keyframes mist-drift-d { 0% { transform: translate3d(0,0,0) scale(1);    opacity: 0.78; }
                                  100% { transform: translate3d(-4%, -2%, 0) scale(1.10); opacity: 0.58; } }
        @keyframes mist-drift-e { 0% { transform: translate3d(0,0,0) scale(1);    opacity: 0.70; }
                                  100% { transform: translate3d(3%, 1%, 0) scale(1.06); opacity: 0.52; } }
        @keyframes mist-drift-f { 0% { transform: translate3d(0,0,0) scale(1);    opacity: 0.72; }
                                  100% { transform: translate3d(-3%, 3%, 0) scale(1.10); opacity: 0.54; } }
        @media (prefers-reduced-motion: reduce) {
          .ionic-mist { animation: none !important; }
        }
      `}</style>
    </section>
  );
};

export default TerritoryMap;
